import { decorate, inject, injectable } from 'inversify'
import { makeAutoObservable, runInAction, action } from 'mobx'

//evertel
import { APIDataBlueUser, APIDataIntegration, APIDataIntegrationApiKey } from '@evertel/types'
import { Api } from '@evertel/api'
import { IntegrationStore } from '@evertel/stores'
import { DEFAULT_USER_SCOPE, FetchUserService } from '@evertel/blue-user'

export interface APIDataIntegrationWithKeys extends APIDataIntegration {
    apiKeys?: APIDataIntegrationApiKey[]
}
class IntegrationController {
    departmentId = 0

    constructor(
        private api: Api,
        private integrationStore: IntegrationStore,
        private fetchUserService: FetchUserService
    ) {
        makeAutoObservable(this, { init: action })
    }

    init = (departmentId: number) => {
        if (!departmentId) return
        this.departmentId = departmentId
    }

    /**
     * Fetch all integrations for the department
     */
    fetchDepartmentIntegrations = async () => {
        if (!this.departmentId) return

        const integrations = await this.api.Routes.Department.getIntegrations(this.departmentId, {
            include: {
                relation: 'integrationUser',
                scope: {
                    ...DEFAULT_USER_SCOPE
                }
            }
        })

        const apiKeysPerIntegration = await Promise.all(
            integrations.map(i => this.api.Routes.Integration.getApiKeys(i.id, this.filter))
        )

        integrations.forEach((integration: APIDataIntegrationWithKeys, idx) => {
            integration.apiKeys = apiKeysPerIntegration[idx]
        })

        // Store integration users in BlueUserStore so they're accessible like any other user
        const integrationUsers = integrations
            .map(i => i.integrationUser)
            .filter((u): u is APIDataBlueUser => !!u)
        if (integrationUsers.length) {
            this.fetchUserService.addRecentlyFetchedUsers(integrationUsers)
        }

        runInAction(() => {
            this.integrationStore.update(integrations)
        })

        return integrations
    }

    /**
     * Create a new connector for the department
     */
    addConnector = async (data: { name: string, description?: string, serviceType: string }) => {
        if (!this.departmentId) return

        const integration = await this.api.Routes.Department.postIntegrations(this.departmentId, data)

        runInAction(() => {
            this.integrationStore.update(integration)
        })

        return integration
    }

    /**
     * Update an existing connector
     */
    updateConnector = async (integrationId: number, data: { name: string, description?: string, configuration?: any }) => {
        const integration = await this.api.Routes.Integration.putById(
            integrationId,
            data as { name: string, description: string, configuration: any }
        )

        runInAction(() => {
            this.integrationStore.update(integration)
        })

        return integration
    }

    /**
     * Permanently delete a connector and all related data.
     * Only works if the integration has never been used (no event logs).
     * Use disableConnector if the integration has been active.
     */
    deleteConnector = async (integrationId: number) => {
        await this.api.Routes.Integration.delHard(integrationId)

        runInAction(() => {
            this.integrationStore.deleteById(integrationId)
        })
    }

    /**
     * Soft-disable a connector (revokes all API keys, soft-deletes all ACLs).
     * The integration record remains for audit purposes.
     */
    disableConnector = async (integrationId: number) => {
        await this.api.Routes.Integration.putDisable(integrationId)

        runInAction(() => {
            this.integrationStore.deleteById(integrationId)
        })
    }

    /**
     * Create a new API key for the integration
     */
    createApiKey = async (integrationId: number, name: string) => {
        const result = await this.api.Routes.Integration.postApiKey(integrationId, name)
        return result
    }

    /**
     * Revoke an API key by its keyHash
     */
    revokeApiKey = async (integrationId: number, keyHash: string) => {
        await this.api.Routes.Integration.delApiKeyById(integrationId, keyHash)
    }

    /**
     * Fetch ACL entries for the integration (with principal relation for room names)
     */
    fetchAcl = async (integrationId: number) => {
        const acl = await this.api.Routes.Integration.getIntegrationAcl(integrationId, {
            ...this.filter,
            include: 'principal'
        })
        return acl
    }

    /**
     * Fetch a single integration by ID
     */
    fetchIntegration = async (integrationId: number) => {
        const [integration, apiKeys] = await Promise.all([
            this.api.Routes.Integration.getById(integrationId, {
                include: {
                    relation: 'integrationUser',
                    scope: {
                        ...DEFAULT_USER_SCOPE
                    }
                }
            }),
            this.api.Routes.Integration.getApiKeys(integrationId, this.filter)
        ])

        // Store integration user in BlueUserStore
        if (integration.integrationUser) {
            this.fetchUserService.addRecentlyFetchedUsers([integration.integrationUser])
        }

        runInAction(() => {
            (integration as APIDataIntegrationWithKeys).apiKeys = apiKeys
            this.integrationStore.update(integration)
        })

        return integration
    }

    /**
     * Get integration by ID (from store)
     */
    getIntegrationById = (id: number): APIDataIntegrationWithKeys | undefined => {
        return this.integrationStore.findById(id)
    }

    /**
     * Get all integrations for the department
     */
    get departmentIntegrations(): APIDataIntegrationWithKeys[] {
        return this.integrationStore.objectsArray.filter(
            (i) => i.departmentId === this.departmentId
        ) as APIDataIntegrationWithKeys[]
    }

    /**
     * Check if integrations are already loaded
     */
    get hasIntegrations(): boolean {
        return this.integrationStore.objectsArray.some(i => i.departmentId === this.departmentId)
    }

    get filter() {
        return {
            order: 'createdDate DESC'
        }
    }

}

decorate(injectable(), IntegrationController)
decorate(inject(Api), IntegrationController, 0)
decorate(inject(IntegrationStore), IntegrationController, 1)
decorate(inject(FetchUserService), IntegrationController, 2)

export { IntegrationController }
